import PropTypes from 'prop-types';
import React from 'react';
import { Area, Avatar, Typography, Badge } from '@ublend-npm/aulaui-core';
import { Description, Subtitle } from './UserBlockResponsive.styled';

const UserBlockResponsive = ({ name, title, subtitle, avatar, online }) => {
  // Generate consistent avatar configuration using the name
  const displayName = name || title;

  // Create avatar element
  const avatarElement = (
    <Avatar
      kind="circular"
      sizes="20px"
      src={avatar}
      fallbackText={displayName?.substring(0, 1).toUpperCase()}
      sx={{
        color: '#fff',
        backgroundColor: '#5B516C',
      }}
    />
  );

  // Wrap with badge if user is online
  const avatarWithBadge = online ? (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      variant="dot"
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: '#44b700',
          color: '#44b700',
          '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            animation: 'ripple 1.2s infinite ease-in-out',
            border: '1px solid currentColor',
            content: '""',
          },
        },
        '@keyframes ripple': {
          '0%': {
            transform: 'scale(.8)',
            opacity: 1,
          },
          '100%': {
            transform: 'scale(2.4)',
            opacity: 0,
          },
        },
      }}
    >
      {avatarElement}
    </Badge>
  ) : (
    avatarElement
  );

  return (
    <Area
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: 0,
      }}
    >
      {/* Single Avatar with online badge */}
      {avatarWithBadge}

      {/* Inline text next to avatar */}
      <Description>
        <Typography
          fontSize="1rem"
          fontWeight={550}
          sx={{
            // Normal behavior for larger screens
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',

            // Allow wrapping on smaller screens
            '@media (max-width: 600px)': {
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        >
          {title}
        </Typography>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Description>
    </Area>
  );
};

UserBlockResponsive.propTypes = {
  avatar: PropTypes.string,
  boldTitle: PropTypes.bool,
  collapsed: PropTypes.bool,
  correlationId: PropTypes.string,
  name: PropTypes.string,
  online: PropTypes.bool,
  subtitle: PropTypes.node,
  title: PropTypes.string,
  user: PropTypes.shape({
    objectId: PropTypes.string.isRequired,
    avatar: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  isNotFocusable: PropTypes.bool,
  size: PropTypes.string,
};

UserBlockResponsive.defaultProps = {
  avatar: undefined,
  boldTitle: true,
  collapsed: false,
  correlationId: undefined,
  name: undefined,
  online: false,
  subtitle: <span />,
  title: '',
  user: undefined,
  isNotFocusable: false,
  size: 'medium',
};

export default UserBlockResponsive;
